import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Badge,
  Button,
  Empty,
  Input,
  Modal,
  Space,
  Tag,
  Tooltip,
  message,
} from "antd"
import {
  DesktopOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import PanelLayout from "../../Layout/PanelLayout"
import HelpChat from "../../components/HelpChat/HelpChat"
import HostnameLink from "../../components/HostnameLink/HostnameLink"
import StatusTag from "../../components/StatusTag/StatusTag"
import apiClient from "../../Utils/apiClient"
import { useHelpLive } from "../../Utils/useHelpLive"
import { useHeaderContext } from "../../Utils/HeaderContext"
import { activityLabel, formatCommDoing, formatIdle } from "../../Utils/userActivity"
import {
  hasRustdeskId,
  launchRustdesk,
  osLabel,
  queueRemoteAction,
  sessionStatusOf,
} from "../../Utils/remoteSupport"
import { renderDate } from "../../Utils/utils"
import "../Operations/Operations.scss"
import "./HelpDesk.scss"

dayjs.extend(relativeTime)

const FILTERS = [
  { key: "waiting", label: "Needs reply", tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
  { key: "open", label: "Open", tone: "#0891b2", soft: "rgba(56, 189, 248, 0.14)" },
  { key: "closed", label: "Closed", tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
  { key: "all", label: "All chats", tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
]

const initials = (value) => {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return "?"
  return `${parts[0][0] || ""}${parts[1]?.[0] || ""}`.toUpperCase()
}

const timeAgo = (value) => {
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.fromNow() : ""
}

const mergeDevice = (incoming, previous, rows = []) => {
  if (incoming?.device) return incoming.device
  if (previous?.device && String(previous._id) === String(incoming?._id)) {
    return previous.device
  }
  const row = rows.find((item) => String(item._id) === String(incoming?._id))
  return row?.device || previous?.device || null
}

const HelpDesk = () => {
  const navigate = useNavigate()
  const { setTaskId, setRender, updateDeviceTask } = useHeaderContext()
  const [filter, setFilter] = useState("waiting")
  const [threads, setThreads] = useState([])
  const [summary, setSummary] = useState({
    open: 0,
    closed: 0,
    waiting: 0,
    total: 0,
  })
  const [selectedId, setSelectedId] = useState(null)
  const [thread, setThread] = useState(null)
  const [device, setDevice] = useState(null)
  const [searchInput, setSearchInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [listLoading, setListLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [composer, setComposer] = useState("")
  const [remoteBusy, setRemoteBusy] = useState(false)

  const loadList = async (keepId = selectedId) => {
    setListLoading(true)
    try {
      const res = await apiClient("help", "GET", null, { params: { status: "all" } })
      if (!res?.success) throw new Error(res?.message || "Could not load chats")
      const rows = res?.data || []
      setThreads(rows)
      if (res?.summary) setSummary(res.summary)
      if (keepId && rows.some((row) => String(row._id) === String(keepId))) return
      const waiting = rows.find((row) => row.status === "open" && row.unreadForIt > 0)
      const open = rows.find((row) => row.status === "open")
      const next = waiting || open || rows[0]
      if (next?._id) setSelectedId(next._id)
      else {
        setSelectedId(null)
        setThread(null)
        setDevice(null)
      }
    } finally {
      setListLoading(false)
    }
  }

  const loadThread = async (id) => {
    if (!id) return
    setLoading(true)
    try {
      const res = await apiClient(`help/${id}`)
      if (!res?.success) throw new Error(res?.message || "Could not load chat")
      const incoming = res?.thread || null
      setThread((prev) =>
        incoming
          ? { ...incoming, device: mergeDevice(incoming, prev, threads) }
          : null
      )
      setThreads((prev) =>
        prev.map((row) =>
          String(row._id) === String(id) ? { ...row, unreadForIt: 0 } : row
        )
      )
    } catch (err) {
      message.error(err.message || "Could not load chat")
    } finally {
      setLoading(false)
    }
  }

  const loadDevice = async (hostname) => {
    if (!hostname) {
      setDevice(null)
      return
    }
    try {
      const res = await apiClient(`devices/host/${encodeURIComponent(hostname)}`)
      if (res?.hostname) setDevice(res)
      else setDevice(thread?.device || null)
    } catch (err) {
      setDevice(thread?.device || null)
    }
  }

  useEffect(() => {
    loadList().catch((err) => message.error(err.message || "Could not load chats"))
  }, [])

  useEffect(() => {
    setComposer("")
    if (selectedId) loadThread(selectedId)
  }, [selectedId])

  useEffect(() => {
    loadDevice(thread?.hostname)
  }, [thread?._id, thread?.hostname])

  useHelpLive((incoming) => {
    if (!incoming?._id) return
    setThreads((prev) => {
      const existing = prev.find((row) => String(row._id) === String(incoming._id))
      const next = prev.filter((row) => String(row._id) !== String(incoming._id))
      return [
        {
          ...incoming,
          messages: undefined,
          device: incoming.device || existing?.device || null,
          unreadForIt:
            String(incoming._id) === String(selectedId)
              ? 0
              : incoming.unreadForIt,
        },
        ...next,
      ]
    })
    if (String(incoming._id) === String(selectedId)) {
      setThread((prev) => ({
        ...incoming,
        device: mergeDevice(incoming, prev),
      }))
      apiClient(`help/${incoming._id}`).catch(() => {})
    }
  })

  const counts = useMemo(() => {
    const waiting = threads.filter(
      (row) => row.status === "open" && (row.unreadForIt || 0) > 0
    ).length
    const open = threads.filter((row) => row.status === "open").length
    const closed = threads.filter((row) => row.status === "closed").length
    return {
      waiting,
      open,
      closed,
      all: Math.max(summary.total || 0, threads.length),
    }
  }, [threads, summary])

  const visible = useMemo(() => {
    const q = searchInput.trim().toLowerCase()
    return threads.filter((row) => {
      if (filter === "waiting") {
        if (row.status !== "open" || !(row.unreadForIt > 0)) return false
      } else if (filter === "open" && row.status !== "open") return false
      else if (filter === "closed" && row.status !== "closed") return false
      if (!q) return true
      const blob = [
        row.userName,
        row.userEmail,
        row.hostname,
        row.serialNumber,
        row.lastMessage,
        row.device?.user,
      ]
        .join(" ")
        .toLowerCase()
      return blob.includes(q)
    })
  }, [threads, filter, searchInput])

  const send = async (text) => {
    if (!selectedId) return
    setSending(true)
    try {
      const res = await apiClient(`help/${selectedId}/messages`, "POST", { text })
      if (!res?.success) throw new Error(res?.message || "Could not send")
      setComposer("")
      setThread((prev) => ({
        ...(res?.thread || prev),
        device: mergeDevice(res?.thread, prev, threads),
      }))
      await loadList(selectedId)
    } catch (err) {
      message.error(err.message || "Could not send")
    } finally {
      setSending(false)
    }
  }

  const suggestReply = async () => {
    if (!selectedId) return
    setSuggesting(true)
    try {
      const res = await apiClient("assistant/draft-reply", "POST", {
        threadId: selectedId,
      })
      if (res?.offline || !res?.success) {
        throw new Error(res?.message || "local model offline")
      }
      if (res.draft) setComposer(res.draft)
      else message.warning("The local model did not return a draft")
    } catch (err) {
      message.error(err.message || "Could not draft a reply")
    } finally {
      setSuggesting(false)
    }
  }

  const closeChat = () => {
    if (!selectedId) return
    Modal.confirm({
      title: "Close this chat?",
      content: "The user can still reopen it from My workspace by sending another message.",
      okText: "Close chat",
      onOk: async () => {
        try {
          const res = await apiClient(`help/${selectedId}/close`, "POST")
          setThread((prev) => ({
            ...(res?.thread || prev),
            device: mergeDevice(res?.thread, prev, threads),
          }))
          await loadList(selectedId)
        } catch (err) {
          message.error(err.message || "Could not close chat")
        }
      },
    })
  }

  const reopenChat = async () => {
    if (!selectedId) return
    try {
      const res = await apiClient(`help/${selectedId}/reopen`, "POST")
      setThread((prev) => ({
        ...(res?.thread || prev),
        device: mergeDevice(res?.thread, prev, threads),
      }))
      await loadList(selectedId)
      message.success("Chat reopened")
    } catch (err) {
      message.error(err.message || "Could not reopen chat")
    }
  }

  const startRemote = async () => {
    if (!device?.hostname) return
    setRemoteBusy(true)
    try {
      await queueRemoteAction({
        device,
        action: "connect",
        setTaskId,
        setRender,
        updateDeviceTask,
      })
    } catch (err) {
      message.error(err.message || "Could not start remote access")
    } finally {
      setRemoteBusy(false)
    }
  }

  const contextDevice = device || thread?.device
  const commDoing = formatCommDoing(contextDevice || {})
  const doingLabel = commDoing
    ? [commDoing.main, ...commDoing.extras].filter(Boolean).join(" · ")
    : [
        contextDevice?.appName ||
          activityLabel(
            contextDevice?.primaryActivity || contextDevice?.activityLabel
          ),
        contextDevice?.idleSeconds
          ? formatIdle(contextDevice.idleSeconds)
          : "",
      ]
        .filter(Boolean)
        .join(" · ")
  const waitingOnIt = (thread?.unreadForIt || 0) > 0 && thread?.status === "open"
  const remoteReady = hasRustdeskId(device)
  const session = sessionStatusOf(device)

  return (
    <PanelLayout
      title="Help desk"
      subtitle="Chat with users and resolve endpoint support requests."
      actions={
        <Button
          icon={<ReloadOutlined />}
          loading={listLoading}
          onClick={() =>
            loadList(selectedId).catch((err) =>
              message.error(err.message || "Could not load chats")
            )
          }
        >
          Refresh
        </Button>
      }
    >
      <div className="ops-kpis">
        {FILTERS.map((item) => (
          <button
            type="button"
            key={item.key}
            className={`ops-kpi${filter === item.key ? " is-active" : ""}`}
            style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
            onClick={() => setFilter(item.key)}
          >
            <div>
              <span className="ops-kpi-label">{item.label}</span>
              <strong>{counts[item.key] ?? 0}</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="help-desk">
        <aside className="help-desk__inbox">
          <div className="ops-toolbar help-desk__toolbar">
            <span className="ops-toolbar__count">
              {visible.length} chat{visible.length === 1 ? "" : "s"}
              {filter !== "all"
                ? ` · ${FILTERS.find((item) => item.key === filter)?.label}`
                : ""}
            </span>
          </div>
          <Input
            prefix={<SearchOutlined />}
            allowClear
            placeholder="Search user, PC, or message"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="help-desk__list">
            {listLoading && !threads.length ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Loading chats…"
              />
            ) : !visible.length ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  searchInput
                    ? "No chats match that search"
                    : filter === "waiting"
                      ? "Nothing waiting on IT"
                      : "No chats yet"
                }
              />
            ) : (
              visible.map((row) => {
                const needsReply = row.status === "open" && (row.unreadForIt || 0) > 0
                return (
                  <button
                    key={row._id}
                    type="button"
                    className={`help-desk__item${
                      String(row._id) === String(selectedId)
                        ? " help-desk__item--active"
                        : ""
                    }${needsReply ? " help-desk__item--unread" : ""}`}
                    onClick={() => setSelectedId(row._id)}
                  >
                    <div className="help-desk__avatar">
                      {initials(row.userName || row.userEmail)}
                    </div>
                    <div className="help-desk__item-body">
                      <div className="help-desk__item-top">
                        <strong>{row.userName || row.userEmail || "User"}</strong>
                        <span>{timeAgo(row.lastMessageAt)}</span>
                      </div>
                      <div className="help-desk__item-meta">
                        <span
                          className={`inv-device__pulse inv-device__pulse--${String(
                            row.device?.deviceStatus || "offline"
                          )
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                          aria-hidden
                        />
                        <span className="ops-name">{row.hostname || "No device"}</span>
                        {row.device?.deviceStatus ? (
                          <StatusTag status={row.device.deviceStatus} />
                        ) : null}
                      </div>
                      <div className="help-desk__preview">
                        {row.lastMessage || "New chat"}
                      </div>
                      <div className="help-desk__item-tags">
                        {needsReply ? <Tag color="gold">Needs reply</Tag> : null}
                        {row.status === "closed" ? <Tag>Closed</Tag> : null}
                        {(row.unreadForIt || 0) > 0 ? (
                          <Badge count={row.unreadForIt} size="small" />
                        ) : null}
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </aside>

        <section className="help-desk__pane">
          {thread ? (
            <>
              <div className="help-desk__header">
                <div>
                  <div className="help-desk__title">
                    {thread.userName || thread.userEmail || "User"}
                    {thread.status === "closed" ? (
                      <Tag className="ms-2">Closed</Tag>
                    ) : waitingOnIt ? (
                      <Tag className="ms-2" color="gold">
                        Waiting on IT
                      </Tag>
                    ) : (
                      <Tag className="ms-2" color="blue">
                        Waiting on user
                      </Tag>
                    )}
                  </div>
                  <div className="help-desk__sub">
                    {thread.userEmail || "No email"}
                    {thread.hostname ? ` · ${thread.hostname}` : " · No device linked"}
                  </div>
                </div>
                <Space wrap>
                  {thread.status === "closed" ? (
                    <Button onClick={reopenChat}>Reopen</Button>
                  ) : (
                    <Button onClick={closeChat}>Close chat</Button>
                  )}
                </Space>
              </div>
              <div className="help-desk__body">
                <HelpChat
                  thread={thread}
                  staffView
                  sending={sending || loading}
                  onSend={send}
                  placeholder="Reply to this user…"
                  draftValue={composer}
                  onDraftValue={setComposer}
                  extraActions={
                    <Button
                      loading={suggesting}
                      disabled={!selectedId || sending}
                      onClick={suggestReply}
                    >
                      Suggest reply
                    </Button>
                  }
                />
                <aside className="help-desk__context">
                  <h3>Person</h3>
                  <dl className="help-desk__facts">
                    <div>
                      <dt>Name</dt>
                      <dd>{thread.userName || "—"}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{thread.userEmail || "—"}</dd>
                    </div>
                    <div>
                      <dt>Opened</dt>
                      <dd>{renderDate(thread.createdAt)}</dd>
                    </div>
                  </dl>

                  <h3>Device</h3>
                  {contextDevice || thread.hostname ? (
                    <>
                      <dl className="help-desk__facts">
                        <div>
                          <dt>PC</dt>
                          <dd>
                            <HostnameLink record={contextDevice || {}}>
                              {thread.hostname || contextDevice?.hostname || "—"}
                            </HostnameLink>
                          </dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd>
                            <StatusTag
                              status={contextDevice?.deviceStatus || "Offline"}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt>User on PC</dt>
                          <dd>{contextDevice?.user || "—"}</dd>
                        </div>
                        <div>
                          <dt>OS</dt>
                          <dd>{osLabel(contextDevice) || "—"}</dd>
                        </div>
                        <div>
                          <dt>Doing</dt>
                          <dd>{doingLabel || "—"}</dd>
                        </div>
                        <div>
                          <dt>Last seen</dt>
                          <dd>
                            {renderDate(contextDevice?.lastReportReceivedTime)}
                          </dd>
                        </div>
                        <div>
                          <dt>Serial</dt>
                          <dd>{thread.serialNumber || contextDevice?.serialNumber || "—"}</dd>
                        </div>
                      </dl>
                      <Space wrap className="help-desk__actions">
                        {contextDevice?._id ? (
                          <Button
                            icon={<DesktopOutlined />}
                            onClick={() =>
                              navigate(`/device-management/${contextDevice._id}`)
                            }
                          >
                            Open device
                          </Button>
                        ) : null}
                        {thread.hostname ? (
                          <Button
                            onClick={() =>
                              navigate(
                                `/users-activity?host=${encodeURIComponent(
                                  thread.hostname
                                )}`
                              )
                            }
                          >
                            Activity
                          </Button>
                        ) : null}
                        <Button
                          type="primary"
                          icon={<ThunderboltOutlined />}
                          disabled={!remoteReady || contextDevice?.deviceStatus === "Offline"}
                          loading={remoteBusy}
                          onClick={startRemote}
                        >
                          Start remote
                        </Button>
                        <Tooltip
                          title={
                            remoteReady
                              ? "Open RustDesk on this computer"
                              : "This PC has not reported a RustDesk ID"
                          }
                        >
                          <Button
                            icon={<LinkOutlined />}
                            disabled={!remoteReady}
                            onClick={() => launchRustdesk(device)}
                          >
                            RustDesk
                          </Button>
                        </Tooltip>
                      </Space>
                      {session && session !== "idle" ? (
                        <p className="help-desk__hint">
                          Remote session: <StatusTag status={session} />
                        </p>
                      ) : null}
                      {!remoteReady ? (
                        <p className="help-desk__hint">
                          Remote access needs a RustDesk ID on the device. Open
                          the device page if you need to set that up.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="help-desk__hint">
                      This chat is not linked to a PC. Ask the user which
                      hostname they are on.
                    </p>
                  )}
                </aside>
              </div>
            </>
          ) : (
            <Empty
              description="Select a chat. Users start these from My workspace or a device Help request."
            />
          )}
        </section>
      </div>
    </PanelLayout>
  )
}

export default HelpDesk
